import { useEffect } from 'react';

import { getSessionId } from 'state/amplitude/amplitude-package';
import { RunningExperiments, useExperiments } from 'state/experiments';

import {
  clearOrderPendingProcessModalExposureSessionsForTests,
  hasHandledOrderPendingProcessModalExposureSession,
  markOrderPendingProcessModalExposureSessionHandled,
} from './order-pending-process-modal-exposure-sessions';

const ORDER_PENDING_PROCESS_MODAL_EXPOSURE_DEDUPE_PREFIX = 'amplitude-session:';

export const getOrderPendingProcessModalExperimentSessionDedupeKey = (sessionId?: string | null) =>
  sessionId ? `${ORDER_PENDING_PROCESS_MODAL_EXPOSURE_DEDUPE_PREFIX}${sessionId}` : undefined;

export const clearOrderPendingProcessModalExperimentExposureSessionsForTests = () => {
  clearOrderPendingProcessModalExposureSessionsForTests();
};

export const useOrderPendingProcessModalExperimentVariant = () => {
  const { getVariantValue } = useExperiments();

  return getVariantValue(RunningExperiments.OrderPendingProcessModal)?.value;
};

/**
 * Confirmed treatment only. Use for actions that should only start for guests actually
 * bucketed into treatment, e.g. persisting a new pending-submitted-order record.
 * Defaults to control when the variant is unresolved so checkout remains safe if Amplitude is slow.
 */
export const useEnableOrderPendingProcessModalExperiment = () => {
  const variantValue = useOrderPendingProcessModalExperimentVariant();

  return variantValue === 'treatment';
};

/**
 * Treatment OR unresolved. Use for guarding/recovering an *existing* pending-submitted-order
 * (recovery UI, back-navigation guards, duplicate-submit prevention): once an order is already
 * in flight, losing that safety net to a slow or failed Amplitude fetch on a cold reload is worse
 * than running it for a guest who later turns out to be control. `isLoading` alone isn't enough:
 * ExperimentsProvider still flips it to false after a failed fetch, leaving the variant
 * `undefined` rather than a confirmed non-treatment value, so treat any unresolved variant
 * (loading or not) as unresolved and only disable once a concrete non-treatment value is known.
 */
export const useEnableOrderPendingProcessModalExperimentOrUnresolved = () => {
  const { isLoading } = useExperiments();
  const variantValue = useOrderPendingProcessModalExperimentVariant();

  return isLoading || variantValue === undefined || variantValue === 'treatment';
};

/**
 * Logs experiment exposure once per Amplitude session from checkout payment surfaces.
 */
export const useExposeOrderPendingProcessModalExperiment = (shouldExpose = true) => {
  const { logExposure } = useExperiments();
  const variantValue = useOrderPendingProcessModalExperimentVariant();
  const amplitudeSessionId = getSessionId()?.toString();

  useEffect(() => {
    const dedupeKey = getOrderPendingProcessModalExperimentSessionDedupeKey(amplitudeSessionId);

    if (
      !shouldExpose ||
      !variantValue ||
      !dedupeKey ||
      hasHandledOrderPendingProcessModalExposureSession(dedupeKey)
    ) {
      return;
    }

    logExposure(RunningExperiments.OrderPendingProcessModal, { dedupeKey });
    markOrderPendingProcessModalExposureSessionHandled(dedupeKey);
  }, [amplitudeSessionId, logExposure, shouldExpose, variantValue]);
};
